import { Classes, TabsProps } from "@blueprintjs/core";
import React from "react";
import styled from "styled-components";

import { Spacer } from "../Spacer";

import { InternalHexTabs } from "./InternalHexTabs.js";

export interface HexTabsProps extends TabsProps {}

/**
 * See [Blueprint Tabs documentation](https://blueprintjs.com/docs/#core/components/tabs)
 */
export const HexTabs = styled(
  // cast because `styled()` doesn't mesh well with Blueprint's class component signature
  InternalHexTabs as React.ComponentType<HexTabsProps>,
).attrs({ animate: false })`
  && {
    .${Classes.TAB} {
      .${Classes.ICON} > svg {
        color: inherit;
      }

      &,
      &:visited,
      &:hover,
      &:active {
        text-decoration: none;
      }
    }

    &.${Classes.VERTICAL} {
      .${Classes.TAB_LIST} {
        align-items: stretch;

        li:not(:last-child) {
          margin-right: 0;
        }

        .${Classes.TAB} {
          margin-right: 0;
          padding: 4px 8px;
          gap: 6px;

          ${Spacer} {
            margin-inline: -6px auto;
          }

          color: ${({ theme }) => theme.fontColor.DEFAULT};

          line-height: 20px;

          transition: background-color
            ${({ theme }) =>
              `${theme.animation.duration} ${theme.animation.easing}`}
            color
            ${({ theme }) =>
              `${theme.animation.duration} ${theme.animation.easing}`};

          /* Not selected or disabled */
          &[aria-selected="false"][aria-disabled="false"] {
            .${Classes.ICON} {
              color: ${({ theme }) => theme.iconColor};
            }
          }

          /* Selected */
          &[aria-selected="true"] {
            color: ${({ theme }) => theme.menuItem.activeText};

            background-color: ${({ theme }) => theme.menuItem.activeBackground};
          }

          /* Disabled */
          &[aria-disabled="true"] {
            color: ${({ theme }) => theme.fontColor.MUTED};
          }

          &:hover:not([aria-disabled="true"]):not([aria-selected="true"]) {
            color: ${({ theme }) => theme.fontColor.DEFAULT};

            background-color: ${({ theme }) => theme.hoverColor} !important;
          }
        }

        .${Classes.DIVIDER} {
          margin: 8px 4px;

          border-color: ${({ theme }) => theme.borderColor.MUTED};
        }
      }
    }

    &:not(.${Classes.VERTICAL}) {
      .${Classes.TAB_LIST} {
        gap: 20px;

        box-shadow: inset 0 -1px 0 0 ${({ theme }) => theme.borderColor.MUTED};

        transition: box-shadow
          ${({ theme }) =>
            `${theme.animation.duration} ${theme.animation.easing}`}
          color
          ${({ theme }) =>
            `${theme.animation.duration} ${theme.animation.easing}`};

        .${Classes.TAB} {
          margin-right: 0;
          gap: 6px;

          color: ${({ theme }) => theme.fontColor.DEFAULT};
          font-size: ${({ theme }) => theme.fontSize.SMALL};

          /* Not selected */
          &[aria-selected="false"] {
            color: ${({ theme }) => theme.fontColor.MUTED};
          }

          /* Selected */
          &[aria-selected="true"] {
            color: ${({ theme }) => theme.intent.PRIMARY};

            box-shadow: inset 0 -1px 0 ${({ theme }) => theme.intent.PRIMARY};
          }

          &:hover:not([aria-disabled="true"]):not([aria-selected="true"]) {
            color: ${({ theme }) => theme.intent.PRIMARY};
          }
        }

        &.${Classes.LARGE} > .${Classes.TAB} {
          font-size: ${({ theme }) => theme.fontSize.DEFAULT};
          line-height: 40px;
        }

        .${Classes.TAB_INDICATOR} {
          height: 1px;
        }
      }

      &.${Classes.SMALL} {
        > .${Classes.TAB_LIST} {
          gap: 10px;
        }

        .${Classes.TAB} {
          font-size: ${({ theme }) => theme.fontSize.SMALL};
          line-height: 26px;
        }
      }
    }
  }
`;
